import { useRef } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { Skeleton } from "../../components/ui/skeleton";
import { formatDateTime } from "../../lib/format";
import { useForexAdmin } from "./api";
import { ClientTrades, MasterTrades } from "./FinanceTrades";
import { round2 } from "./logic";
import {
  FilterBar,
  FilterField,
  Money,
  Panel,
  QueryState,
  Stat,
  TabStrip,
  mono,
  muted,
  useListState,
  useOpen,
  useTab,
} from "./parts";

// Company finance (027): what Ginox made from trading over a period of MT5
// server days, and client money beside company money now. Every word is the
// glossary's. A summary and its breakdowns share one mirror read for 30 s.

const AMBER = "border-amber-500/30 bg-amber-500/[0.06]";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "all", label: "All time" },
  { value: "", label: "Custom range (days below)" },
];
const BOOKS = [
  { value: "", label: "Both books" },
  { value: "a", label: "A-book" },
  { value: "b", label: "B-book" },
];
const BOOK_LABEL = { a: "A-book", b: "B-book" };
const SIDE = { client: "Client account", cover: "Cover", lp: "LP account" };
const EMPTY = { period: "month", from: "", to: "", tier: "", symbol: "", book: "", lpAccount: "", login: "" };
const FILTERS = ["tier", "symbol", "book", "lpAccount"];

// A period or from and to, never both (a 400 otherwise); clean() drops the blanks.
const queryOf = (f) => (f.from || f.to ? { ...f, period: "" } : { ...f, from: "", to: "" });

const at = (v) => formatDateTime(v);
const Done = ({ v }) =>
  v ? <span className="text-sm text-emerald-400">Yes</span> : <span className="text-sm text-amber-400">No, figures left out</span>;
// Markup and difference run to twenty places; two shown, the whole in the tooltip.
const Rounded = ({ v }) => <span title={v}><Money value={round2(v)} /></span>;
const Users = ({ side }) => (
  <>
    {side.users} <span className="text-xs text-muted-foreground">users</span>
    <span className="block text-sm"><Money value={side.clientPnl} /></span>
  </>
);

function Refused({ list, what }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {list.length === 0 ? `Nothing left out of ${what}.` : `Leaves out ${list.length} account${list.length === 1 ? "" : "s"} not in USD:`}
      </p>
      {list.length > 0 && (
        <QueueTable
          columns={[{ label: "Account" }, { label: "Side" }, { label: "Currency" }]}
          rows={list}
          renderRow={(r) => (
            <tr key={`${r.side}-${r.account}`}>
              <Td className={mono}>{r.account}</Td>
              <Td className="text-xs">{SIDE[r.side] ?? r.side}</Td>
              <Td className={mono}>{r.currency ?? "Unknown"}</Td>
            </tr>
          )}
        />
      )}
    </div>
  );
}

// incomplete (027, 049): an LP copy has not reached the period's end, or failed.
function Freshness({ incomplete, accounts, whole }) {
  if (!incomplete) return null;
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm text-amber-400 ${AMBER}`}>
      Company PnL is partial:{" "}
      {accounts.map((a) => (
        <span key={a.account} className="mr-3">
          {a.name} {a.failing ? "LP copy failing" : a.copiedUntil ? `LP figures still copying, copied until ${at(a.copiedUntil)}` : "never copied"}.
        </span>
      ))}
      {whole}
    </div>
  );
}

const LINES = [
  ["markup", "Markup"],
  ["commission", "Commission"],
  ["swap", "Swap"],
  ["bBookTradingResult", "B-book trading result"],
  ["hedgeResult", "Hedge result"],
  ["lpCosts", "LP costs"],
  ["unexplained", "Unexplained"],
];

// Company PnL (049): seven lines that add up to the total. Signal masters'
// income is not part of it and still comes from the summary.
function Overview({ d, summary }) {
  const c = d.companyPnl;
  const cl = d.clients.clientPnl;
  const { users } = d.clients;
  const { accounts, net } = d.unplaced;
  return (
    <div className="space-y-8">
      <Freshness incomplete={d.lpCopy.incomplete} accounts={d.lpCopy.accounts} whole="Client PnL and users are whole." />
      {!c.complete && (
        <div className={`rounded-xl border px-4 py-3 text-amber-400 ${AMBER}`}>
          <Refused list={d.pnlRefused} what="PnL" />
        </div>
      )}

      <Panel title="Company PnL">
        <div className={`space-y-3 ${d.lpCopy.incomplete ? "opacity-50" : ""}`}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="Company PnL"><Money value={c.total} /></Stat>
            <Stat label="Check"><Money value={c.check} /></Stat>
            <Stat label="Complete"><Done v={c.complete} /></Stat>
          </div>
          <div className="divide-y divide-border/60 rounded-xl border border-border/60">
            {LINES.map(([k, label]) => (
              <div key={k} className="flex justify-between px-4 py-2 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className={mono}><Money value={c[k]} /></span>
              </div>
            ))}
            <div className="flex justify-between px-4 py-2 text-sm font-medium">
              <span>Company PnL</span>
              <span className={mono}><Money value={c.total} /></span>
            </div>
          </div>
          {accounts > 0 && (
            <p className="text-xs text-muted-foreground">
              {accounts} unplaced account{accounts === 1 ? "" : "s"} in the figures, net <Money value={net} />
            </p>
          )}
        </div>
      </Panel>

      <Panel title="Client PnL and users">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Stat label="Client PnL"><Money value={cl.total} /></Stat>
          <Stat label="Profit"><Money value={cl.profit} /></Stat>
          <Stat label="Commission"><Money value={cl.commission} /></Stat>
          <Stat label="Swap"><Money value={cl.swap} /></Stat>
          <Stat label="Fee"><Money value={cl.fee} /></Stat>
          <Stat label="Complete"><Done v={cl.complete} /></Stat>
          <Stat label="Users in profit"><Users side={users.inProfit} /></Stat>
          <Stat label="Users in loss"><Users side={users.inLoss} /></Stat>
          <Stat label="Floating PnL: open positions, now"><Money value={d.clients.floatingPnl} /></Stat>
        </div>
        <p className="text-xs text-muted-foreground">
          Users are judged by trades closed in the period, so the two sides need not add up to client PnL.
        </p>
      </Panel>

      <Panel title="LP copy">
        <QueueTable
          columns={[{ label: "Account" }, { label: "Name" }, { label: "Last copied" }, { label: "Copied until" }, { label: "Copy failing" }]}
          rows={d.lpCopy.accounts}
          renderRow={(a) => (
            <tr key={a.account}>
              <Td className={mono}>{a.account}</Td>
              <Td className="text-xs">{a.name}</Td>
              <Td className={muted}>{at(a.lastCopiedAt)}</Td>
              <Td className={muted}>{at(a.copiedUntil)}</Td>
              <Td className={`text-xs ${a.failing ? "text-red-400" : ""}`}>{a.failing ? "Failing" : "No"}</Td>
            </tr>
          )}
        />
      </Panel>

      <Panel title="Signal masters">
        <QueryState q={summary}>
          {(s) => (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat label="What signal masters earned"><Money value={s.companyPnl.signalMastersEarned} /></Stat>
              <Stat label="Signal masters access fees"><Money value={s.companyPnl.signalMastersAccessFees} /></Stat>
              <Stat label="Signal masters awaiting release"><Money value={s.companyPnl.signalMastersAwaitingRelease} /></Stat>
            </div>
          )}
        </QueryState>
      </Panel>
    </div>
  );
}

const LP_COLUMNS = [
  "Account", "Name", "PnL", "Profit", "Commission", "Swap", "Fee", "Lots", "Trades", "Currency",
  "Balance", "Equity", "Balance read", "Last copied", "Copied until", "Copy failing",
].map((label, i) => ({ label, align: i >= 2 && i <= 11 && i !== 9 ? "right" : undefined }));

const lpRow = (a) => (
  <tr key={a.account}>
    <Td className={mono}>{a.account}</Td>
    <Td className="text-xs">{a.name}</Td>
    <Td align="right"><Money value={a.pnl} /></Td>
    <Td align="right"><Money value={a.profit} /></Td>
    <Td align="right"><Money value={a.commission} /></Td>
    <Td align="right"><Money value={a.swap} /></Td>
    <Td align="right"><Money value={a.fee} /></Td>
    <Td align="right" className={mono}>{a.lots}</Td>
    <Td align="right" className={mono}>{a.trades}</Td>
    <Td className={mono}>{a.currency ?? "—"}</Td>
    {/* Last copied, now; null before the first copy. */}
    <Td align="right"><Money value={a.balance} /></Td>
    <Td align="right"><Money value={a.equity} /></Td>
    <Td className={muted}>{at(a.balanceReadAt)}</Td>
    <Td className={muted}>{at(a.lastCopiedAt)}</Td>
    <Td className={muted}>{at(a.copiedUntil)}</Td>
    <Td className={`text-xs ${a.failing ? "text-red-400" : ""}`}>{a.failing ? "Failing" : "No"}</Td>
  </tr>
);

// The LP & Money tab, from the summary.
function Summary({ d, part }) {
  const { clients, company, refused } = d.money;
  return (
    <div className="space-y-8">
      <Freshness incomplete={d.incomplete} accounts={d.lpAccounts} whole="Client PnL, users and money are whole." />

      {part === "lp-money" && (
        <>
          <Panel title="LP accounts">
            <QueueTable columns={LP_COLUMNS} rows={d.lpAccounts} renderRow={lpRow} />
          </Panel>

          <Panel title="Unmatched fills">
            <p className="text-xs text-muted-foreground">
              LP buys and sells no client or cover trade on the server explains. Above zero is worth a look.
            </p>
            <QueueTable
              columns={[{ label: "Account" }, { label: "Name" }, { label: "Fills", align: "right" }, { label: "Lots", align: "right" }]}
              rows={d.unmatchedFills}
              renderRow={(f) => (
                <tr key={f.account}>
                  <Td className={mono}>{f.account}</Td>
                  <Td className="text-xs">{f.name}</Td>
                  <Td align="right" className={`${mono} ${f.fills > 0 ? "text-amber-400" : ""}`}>{f.fills}</Td>
                  <Td align="right" className={mono}>{f.lots}</Td>
                </tr>
              )}
            />
          </Panel>

          <Panel title="Money now: never filtered, never summed together">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Client money</h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Stat label="Balance"><Money value={clients.balance} /></Stat>
                  <Stat label="Credit"><Money value={clients.credit} /></Stat>
                  <Stat label="Equity"><Money value={clients.equity} /></Stat>
                  <Stat label="Accounts">{clients.accounts}</Stat>
                  <Stat label="Complete"><Done v={clients.complete} /></Stat>
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Company money</h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Stat label="Equity"><Money value={company.equity} /></Stat>
                  <Stat label="Covers equity"><Money value={company.covers.equity} /></Stat>
                  <Stat label="Complete"><Done v={company.complete} /></Stat>
                </div>
                <QueueTable
                  columns={[{ label: "LP account" }, { label: "Name" }, { label: "Equity", align: "right" }, { label: "Read at" }]}
                  rows={company.lpAccounts}
                  renderRow={(a) => (
                    <tr key={a.account}>
                      <Td className={mono}>{a.account}</Td>
                      <Td className="text-xs">{a.name}</Td>
                      <Td align="right"><Money value={a.equity} /></Td>
                      <Td className={muted}>{at(a.readAt)}</Td>
                    </tr>
                  )}
                />
                <QueueTable
                  columns={[{ label: "Cover login" }, { label: "Group now" }, { label: "Equity", align: "right" }]}
                  rows={company.covers.accounts}
                  emptyText="No covers."
                  renderRow={(a) => (
                    <tr key={a.login}>
                      <Td className={mono}>{a.login}</Td>
                      <Td className={muted}>{a.group ?? "—"}</Td>
                      <Td align="right"><Money value={a.equity} /></Td>
                    </tr>
                  )}
                />
              </div>
            </div>
            <Refused list={refused} what="money" />
          </Panel>

          <Panel title="Left out of client and company PnL">
            <Refused list={d.pnlRefused} what="PnL" />
          </Panel>
        </>
      )}
    </div>
  );
}

const PAGES = [
  { value: "overview", label: "Overview" },
  { value: "lp-money", label: "LP & Money" },
  { value: "breakdown", label: "Breakdown" },
  { value: "trades", label: "Client trades" },
  { value: "master-trades", label: "Master trades" },
];
const LISTS = { trades: "/finance/admin/trades", "master-trades": "/finance/admin/master-trades" };

const SPLITS = [
  { value: "tier", label: "By tier" },
  { value: "symbol", label: "By symbol" },
  { value: "book", label: "By book" },
  { value: "lpAccount", label: "By LP account" },
  { value: "day", label: "By day" },
];

const FIGURE_COLUMNS = [
  "Company PnL", "LP PnL", "Markup", "Commission", "B-book result", "LP swap",
  "Signal masters earned", "Signal masters access fees", "Difference", "Company complete",
  "Signal masters awaiting release (not in total)",
  "Client PnL", "Profit", "Commission", "Swap", "Fee", "Client complete",
  "Users in profit", "In profit PnL", "Users in loss", "In loss PnL",
].map((label) => ({ label, align: "right" }));

function figureRow(label, f, strong) {
  const c = f.companyPnl;
  const cl = f.clientPnl;
  return (
    <tr key={label} className={strong ? "font-semibold" : "hover:bg-white/[0.02]"}>
      <Td className={mono}>{label}</Td>
      <Td align="right"><Money value={c.total} /></Td>
      <Td align="right"><Money value={c.lpPnl} /></Td>
      <Td align="right"><Rounded v={c.markup} /></Td>
      <Td align="right"><Money value={c.commission} /></Td>
      <Td align="right"><Money value={c.bBookResult} /></Td>
      <Td align="right"><Money value={c.lpSwap} /></Td>
      <Td align="right"><Money value={c.signalMastersEarned} /></Td>
      <Td align="right"><Money value={c.signalMastersAccessFees} /></Td>
      <Td align="right"><Rounded v={c.difference} /></Td>
      <Td align="right"><Done v={c.complete} /></Td>
      <Td align="right"><Money value={c.signalMastersAwaitingRelease} /></Td>
      <Td align="right"><Money value={cl.total} /></Td>
      <Td align="right"><Money value={cl.profit} /></Td>
      <Td align="right"><Money value={cl.commission} /></Td>
      <Td align="right"><Money value={cl.swap} /></Td>
      <Td align="right"><Money value={cl.fee} /></Td>
      <Td align="right"><Done v={cl.complete} /></Td>
      <Td align="right" className={mono}>{f.users.inProfit.users}</Td>
      <Td align="right"><Money value={f.users.inProfit.clientPnl} /></Td>
      <Td align="right" className={mono}>{f.users.inLoss.users}</Td>
      <Td align="right"><Money value={f.users.inLoss.clientPnl} /></Td>
    </tr>
  );
}

function Breakdown({ by, params }) {
  const q = useForexAdmin("/finance/admin/breakdown", { ...params, by });
  if (q.error) return <ErrorBanner error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton className="h-40 w-full" />;
  const d = q.data;
  const name = (key) =>
    by === "book" ? BOOK_LABEL[key] ?? key : by === "lpAccount" ? d.lpAccounts.find((a) => a.account === key)?.name ?? key : key;
  const rows = [
    ...d.groups.map((g) => [name(g.key), g]),
    ["Not split", d.unsplit],
    ...(d.unknown ? [["Unknown group", d.unknown]] : []),
    ["Total", d.total, true],
  ];
  return (
    <div className="space-y-3">
      <Freshness incomplete={d.incomplete} accounts={d.lpAccounts} whole="Client PnL, users and money are whole." />
      <p className="text-xs text-muted-foreground">
        Split by {d.by}, server days {d.from ?? "the first client trade"} to {d.to}. LP copies{" "}
        {d.incomplete ? "incomplete" : "caught up"}:{" "}
        {d.lpAccounts.map((a) => (
          <span key={a.account} className="mr-3">
            {a.name} ({a.account}) last copied {at(a.lastCopiedAt)}, copied until {at(a.copiedUntil)}
            {a.failing ? ", failing" : ""}.
          </span>
        ))}
      </p>
      <QueueTable
        columns={[{ label: SPLITS.find((t) => t.value === by).label.replace("By ", "") }, ...FIGURE_COLUMNS]}
        rows={rows}
        renderRow={([label, f, strong]) => figureRow(label, f, strong)}
      />
      <p className="text-xs text-muted-foreground">
        Groups, Not split and Unknown group add up to Total for company and client PnL. Users do not: a user counts in every
        group they traded in and once in Total.
      </p>
      <Refused list={d.pnlRefused} what="PnL" />
    </div>
  );
}

export default function Finance() {
  const s = useListState(EMPTY);
  // The page tab lives in ?tab=, the breakdown split in ?by=; filters stay put.
  const [askedPage, setPage] = useTab("overview");
  const page = PAGES.some((t) => t.value === askedPage) ? askedPage : "overview";
  const [askedBy, setBy] = useOpen("by");
  const by = SPLITS.some((t) => t.value === askedBy) ? askedBy : "tier";
  // login is the trades lists' alone (050); the summary and breakdown would 400 on it.
  const params = queryOf({ ...s.applied, login: "" });
  // The PnL route (049) takes only the period: any filter is a 400, so the
  // Overview drops them, and so does its summary read for the signal masters.
  const overview = page === "overview";
  const periodOnly = queryOf({ period: s.applied.period, from: s.applied.from, to: s.applied.to });
  const pnl = useForexAdmin(overview ? "/finance/admin/pnl" : null, periodOnly);
  // The trades lists (050) take the period, login, symbol and page; any other key is a 400.
  const listPath = LISTS[page] ?? null;
  const { period, from, to, login, symbol } = s.applied;
  // Keep the last page on screen while the next loads, but never the other list's rows.
  const list = useForexAdmin(listPath, { ...queryOf({ period, from, to, login, symbol }), page: s.page }, {
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === listPath ? prev : undefined),
  });
  const summary = useForexAdmin(listPath ? null : "/finance/admin/summary", overview ? periodOnly : params);
  const read = (overview ? pnl : listPath ? list : summary).data;
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;

  // ponytail: LP account names as seen so far, since a filtered summary lists one.
  const lps = useRef(new Map());
  summary.data?.lpAccounts.forEach((a) => lps.current.set(a.account, a.name));
  const lpOptions = [
    { value: "", label: "Every LP account" },
    ...[...lps.current].map(([value, label]) => ({ value, label })),
  ];

  const filtered = FILTERS.filter((k) => s.applied[k]);
  const field = (props) => <FilterField {...props} draft={s.draft} setDraft={s.setDraft} />;

  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Company"
      accent="finance"
      description="What Ginox made from trading over a period, where it came from, and client money beside company money now. In USD."
      statusOptions={PAGES}
      status={page}
      onStatus={(t) => {
        s.setPage(1);
        setPage(t);
      }}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <FilterBar onApply={s.apply} onReset={s.reset}>
        {field({ label: "Period", name: "period", options: PERIODS })}
        {field({ label: "From server day (custom)", name: "from", type: "date" })}
        {field({ label: "To server day (custom)", name: "to", type: "date" })}
        {listPath && field({ label: "Login", name: "login" })}
        {!overview && !listPath && field({ label: "Tier (e.g. ecn)", name: "tier" })}
        {!overview && field({ label: "Symbol (e.g. XAUUSD)", name: "symbol" })}
        {!overview && !listPath && field({ label: "Book", name: "book", options: BOOKS })}
        {!overview && !listPath && field({ label: "LP account", name: "lpAccount", options: lpOptions })}
      </FilterBar>

      <div className="space-y-2 text-xs text-muted-foreground">
        <p>
          MT5 server days, not your local time.
          {read && ` Read ${read.from ?? "the first client trade"} to ${read.to}.`}
        </p>
        {!overview && !listPath && filtered.length > 0 && (
          <p className={`rounded-xl border px-4 py-3 text-amber-400 ${AMBER}`}>
            Filtered by {filtered.map((k) => `${k} ${s.applied[k]}`).join(", ")}. Figures without that key drop out: a tier
            or book filter leaves out the LP accounts and what signal masters earned, so company PnL is only the negative
            of that slice's client PnL; an LP account filter leaves out every client figure. Money is never filtered.
          </p>
        )}
      </div>

      {page === "trades" ? (
        <ClientTrades q={list} onPage={s.setPage} />
      ) : page === "master-trades" ? (
        <MasterTrades q={list} onPage={s.setPage} />
      ) : page === "breakdown" ? (
        <div className="space-y-5">
          <TabStrip tabs={SPLITS} value={by} onChange={setBy} />
          <Breakdown key={by} by={by} params={params} />
        </div>
      ) : overview ? (
        <QueryState q={pnl}>{(d) => <Overview d={d} summary={summary} />}</QueryState>
      ) : (
        <QueryState q={summary}>{(d) => <Summary d={d} part={page} />}</QueryState>
      )}
    </QueueLayout>
  );
}
